import { Button, Image } from '@heroui/react';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import Placeholder from '@/assets/images/placeholder.png';
import useEducation from '@/patient-app-common/hooks/useEducation';
import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import { hasAiChatbotEnabled } from '@/patient-app-common/helpers/education_helpers';
import useSegment from '@/hooks/useSegment';

import { EduChatbot } from './chatbot/EduChatbot';
import { EduList } from './EduList';
import { EduTopic } from './EduTopic';
import styles from './styles/education.module.css';

const SEAMLESS_ANSWERS_OPENED_EVENT = 'Seamless Answers Opened';

export function Education() {
  const { t } = useTranslation('education');
  const [sidebarOverlaps, setSidebarOverlaps] = useState(true);
  const { topicId } = useParams<{ topicId: string }>();
  const { educationForProfile } = useEducation();
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const { sendTrackEvent } = useSegment();

  useEffect(() => {
    const handleResize = () => setPageWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data: patient } = useGetPatientQuery();

  const hasAiChatbot = hasAiChatbotEnabled(patient);

  useEffect(() => {
    if (hasAiChatbot && !topicId) {
      sendTrackEvent(SEAMLESS_ANSWERS_OPENED_EVENT);
    }
  }, [hasAiChatbot, topicId, sendTrackEvent]);

  useEffect(() => {
    if (topicId) {
      setSidebarOverlaps(false);
    }
  }, [topicId]);

  return (
    <div className="-mx-4">
      <div className={styles.withSidebar}>
        <div className={sidebarOverlaps ? '' : 'hidden md:block'}>
          <aside className={styles.sidebar} role="navigation" tabIndex={0}>
            <EduList
              education={educationForProfile}
              hasAiChatbot={hasAiChatbot}
              setSidebarOverlaps={setSidebarOverlaps}
              topicId={topicId}
            />
          </aside>
        </div>
        {topicId && (
          <main className={styles.mainViewport}>
            <EduTopic
              contentContainerRef={contentContainerRef}
              education={educationForProfile}
              topicId={topicId}
              setSidebarOverlaps={setSidebarOverlaps}
              pageWidth={pageWidth}
            />
          </main>
        )}
        {!topicId &&
          (hasAiChatbot ? (
            (pageWidth <= 767 ? !sidebarOverlaps : true) && (
              <EduChatbot
                hasAiConsent={!!patient?.hasAiConsent}
                education={educationForProfile}
                setSidebarOverlaps={setSidebarOverlaps}
                pageWidth={pageWidth}
              />
            )
          ) : (
            <div className="flex flex-col items-center justify-center">
              <Image src={Placeholder} width={300} disableSkeleton />
              <h1 className="text-xl">{t('common.link.library_hint')}</h1>
              {isMobile ? (
                <Button
                  color="primary"
                  onPress={() => setSidebarOverlaps(true)}
                  size="lg"
                  variant="light"
                >
                  {t('education.placeholder_text')}
                </Button>
              ) : (
                <p className="font-light">{t('education.placeholder_text')}</p>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
